import classNames from 'classnames';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { TooltipIcon } from '../Tooltip/TooltipIcon';
import { TreePickerCheckbox } from './TreePickerCheckbox';
import { TreePickerRemove } from './TreePickerRemove';
import type { TreePickerItemWithValue } from './helpers/TreePickerItem';
import { countLeaves } from './helpers/countLeaves';

interface Props<TKey> {
  item: TreePickerItemWithValue<TKey>;
  value: Set<TKey>;
  onChange: (items: Set<TKey>) => void;
  mode: 'available' | 'selected';
  disabled?: boolean;
  expandedNodes: Set<TKey>;
  collapsible: boolean;
  showCounters: boolean;
}

export const TreePickerNode = <TKey extends string>(
  props: Props<TKey>,
): ReactNode => {
  const [collapsed, setCollapsed] = useState(props.collapsible);
  const hasChildren = props.item.children.length > 0;
  const itemsCount = useMemo(() => countLeaves([props.item]), [props.item]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: toggle only on expandedNodes change or collapsible change
  useEffect(() => {
    if (props.collapsible) {
      setCollapsed(!props.expandedNodes.has(props.item.key));
    } else {
      setCollapsed(false);
    }
  }, [props.expandedNodes.size, props.collapsible]);

  return (
    <div
      className='tree-picker-node'
      data-testid={`tree-picker-node-${props.item.label}`}
    >
      <div
        className={classNames(
          'tree-picker-node__label',
          hasChildren &&
            props.collapsible &&
            'tree-picker-node__label--clickable',
          props.collapsible && 'tree-picker-node__label--hover',
        )}
        onClick={props.collapsible ? toggleCollapsed : undefined}
      >
        <div className='tree-picker-node__label-controls'>
          {props.collapsible &&
            (hasChildren ? (
              <Icon glyph={collapsed ? 'fa-caret-right' : 'fa-caret-down'} />
            ) : (
              <div className='tree-picker__icon-placeholder' />
            ))}
          {props.mode === 'available' && !props.disabled && (
            <TreePickerCheckbox
              item={props.item}
              value={props.value}
              onChange={props.onChange}
            />
          )}
        </div>
        <div className='flex flex-col flex-grow'>
          <div className='flex'>
            {props.item.label}{' '}
            {hasChildren && props.showCounters && `(${itemsCount})`}
            {props.item.tooltip !== undefined && (
              <TooltipIcon
                className='ml-1'
                content={props.item.tooltip}
                size='sm'
              />
            )}
          </div>
          {props.item.description && (
            <div className='tree-picker-node__label-description'>
              {props.item.description}
            </div>
          )}
        </div>
        <div className='tree-picker-node__label-controls'>
          {props.mode === 'selected' && !props.disabled && (
            <TreePickerRemove
              item={props.item}
              value={props.value}
              onChange={props.onChange}
            />
          )}
        </div>
      </div>
      {!collapsed &&
        props.item.children.map((item) => (
          <TreePickerNode
            {...props}
            item={item}
            key={item.key}
            collapsible={props.collapsible}
            showCounters={props.showCounters}
          />
        ))}
    </div>
  );
};
